import { FC, useMemo } from 'react';
import { motion } from 'motion/react';
import DefaultLayout from '@/layouts/Default/default';
import { Seo } from '@/components/SEO/Seo';
import { pageMetadata } from '@/config/pageMetadata';
import Link from '@/components/common/Link';
import type { WorkCase } from '@/components/Work/StackedCarousel/types';
import { workProjects } from '@/components/Work/StackedCarousel/workProjects';
import { siteMetadata } from '@/config/seo';

const WorkRebuildPage: FC = () => {
  const seo = pageMetadata.work;
  const structuredData = useMemo(() => {
    const items = workProjects.map((project, index) => {
      const slug = project.caseStudySlug ?? project.id;
      return {
        '@type': 'ListItem',
        position: index + 1,
        name: project.title,
        url: slug ? `${siteMetadata.siteUrl}/work/${slug}` : `${siteMetadata.siteUrl}/work`,
      };
    });
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Turbo Labs case studies',
        itemListOrder: 'http://schema.org/ItemListOrderAscending',
        itemListElement: items,
      },
    ];
  }, [workProjects]);
  return (
    <DefaultLayout hideHeader>
      <Seo {...seo} structuredData={structuredData} />

      <main className="relative min-h-screen overflow-hidden bg-zinc-50 text-zinc-900 transition-colors dark:bg-zinc-950 dark:text-white">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-zinc-100 via-transparent to-zinc-50 dark:from-zinc-900/80 dark:via-zinc-950 dark:to-black" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.35)_0%,rgba(255,255,255,0)_60%)] dark:bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08)_0%,rgba(0,0,0,0)_65%)] mix-blend-soft-light" />

        <div className="relative z-10 mx-auto flex w-full max-w-[1400px] flex-col px-4 pb-24 pt-16 sm:px-8 lg:px-12">
          <div className="mb-12 mt-8 flex items-center gap-6">
            <h1 className="text-5xl font-light text-zinc-900 sm:text-6xl lg:text-[4.5rem] dark:text-white">
              Selected Projects
            </h1>
            <span className="h-px flex-1 bg-zinc-200 dark:bg-white/20" />
          </div>

          <section className="flex flex-col divide-y divide-zinc-200/70 dark:divide-white/10">
            {workProjects.map((project, index) => (
              <WorkProjectCard key={project.id} project={project} index={index + 1} />
            ))}
          </section>
        </div>
      </main>
    </DefaultLayout>
  );
};

interface WorkProjectCardProps {
  project: WorkCase;
  index: number;
}

const buildImpactSummary = (project: WorkCase): string => {
  if (project.impactStatement) {
    return project.impactStatement;
  }
  const scopeLine = project.scope?.[0]?.description?.trim().replace(/\.$/, '') ?? '';
  const okr = project.okrs?.[0];
  if (scopeLine && okr) {
    const normalized = scopeLine.charAt(0).toLowerCase() + scopeLine.slice(1);
    return `We ${normalized}, unlocking ${okr.value} ${okr.label.toLowerCase()}.`;
  }

  if (scopeLine) {
    const normalized = scopeLine.charAt(0).toLowerCase() + scopeLine.slice(1);
    return `We ${normalized}.`;
  }

  const fallback = project.summary?.[0] ?? project.description ?? '';
  return fallback.trim();
};

const WorkProjectCard: FC<WorkProjectCardProps> = ({ project, index }) => {
  const slug = project.caseStudySlug ?? project.id;
  const Logo = project.logo;
  const description = useMemo(() => buildImpactSummary(project), [project]);
  const meta = [project.category, project.date, project.tags[0]].filter(Boolean) as string[];

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-20% 0px' }}
      transition={{ duration: 0.55, ease: [0.2, 0.8, 0.2, 1], delay: index * 0.08 }}
      className="py-10 first:pt-0"
    >
      <div className="grid gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:gap-16 xl:gap-20">
        <div className="group relative min-h-[380px] overflow-hidden rounded-[36px]">
          <div className="absolute inset-0">
            {project.mediaType === 'video' ? (
              <video
                className="h-full w-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                poster={project.poster ?? project.placeholder}
              >
                {project.mediaSources?.map((source) => (
                  <source key={source.src} src={source.src} type={source.type} />
                ))}
                <source src={project.media} />
              </video>
            ) : (
              <img
                src={project.media}
                alt={project.title}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            )}
          </div>
        </div>

        <div className="flex flex-col justify-between gap-6 text-zinc-800 dark:text-white">
          <div className="space-y-4">
            <p className="text-[1.35rem] font-extralight text-zinc-600 sm:text-[1.7rem] dark:text-white/90">
              {description}
            </p>
          </div>

          {slug && (
            <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
              <div className="flex flex-1 items-center overflow-hidden py-4 sm:py-0">
                {Logo ? (
                  <div className="flex h-full min-h-[4.5rem] w-full items-center">
                    <Logo className="h-full max-h-16 w-auto text-zinc-900 dark:text-white" />
                  </div>
                ) : (
                  <span className="text-2xl font-light text-zinc-900 dark:text-white">
                    {project.title}
                  </span>
                )}
              </div>
              <Link
                href={`/work/${slug}`}
                className="group relative inline-flex w-full min-w-min flex-none items-center justify-between gap-4 overflow-hidden rounded-full border border-zinc-900/30 px-5 py-3 text-sm font-medium text-zinc-900 transition hover:border-zinc-900 dark:border-white/30 dark:text-white sm:w-auto"
              >
                <span className="relative z-10 whitespace-nowrap transition-colors group-hover:text-white dark:group-hover:text-zinc-900">
                  View case study
                </span>
                <span className="relative z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-zinc-900 text-white transition-all group-hover:-rotate-12 group-hover:bg-[#F92E5F] dark:bg-white dark:text-zinc-900">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17l10-10" />
                    <path d="M9 7h8v8" />
                  </svg>
                </span>
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-zinc-900 to-zinc-700 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100 dark:from-white dark:to-white/80" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </motion.article>
  );
};

export default WorkRebuildPage;
